import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogOverlay,
	DialogPortal,
} from "../../../components/ui/dialog";
import { X, BookOpen, Globe, Database } from "lucide-react";
import { PromptExamplesModal } from "./PromptExamplesModal";
import KnowledgeBaseSection from "./KnowledgeBaseSection";
import { toast } from 'react-toastify';

const { channels } = require('../../../shared/constants');
const { ipcRenderer } = window.require('electron');

export const GPTSettingsDialog = ({
	isOpen,
	onClose,
	openaiKey,
	temperature,
	topP,
	openaiModel,
	openaiPrompt,
	reasoningEffort,
	verbosity,
	webSearchEnabled,
	knowledgeBaseEnabled,
	instanceId,
	onSave,
}) => {
	const ensureMinimumTemperature = (value) => Math.max(0.01, Number.parseFloat(value) || 0.01);

	const isGpt5Model = (model) => {
		const gpt5Models = ['gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano', 'gpt-5.2', 'gpt-5.1', 'gpt-5.1-2025-11-13', 'gpt-5', 'gpt-5-mini', 'gpt-5-nano'];
		return gpt5Models.includes(model);
	};

	const supportsNoneReasoningEffort = (model) => ['gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano', 'gpt-5.2', 'gpt-5.1', 'gpt-5.1-2025-11-13'].includes(model);

	const getReasoningEffortOptions = (model) => {
		const baseOptions = [
			{ value: 'minimal', label: 'Minimal (Fastest - Few reasoning tokens)' },
			{ value: 'low', label: 'Low (Quick reasoning)' },
			{ value: 'medium', label: 'Medium (Balanced)' },
			{ value: 'high', label: 'High (Complex tasks)' },
		];

		if (supportsNoneReasoningEffort(model)) {
			return [
				{ value: 'none', label: 'None (No reasoning - Lowest latency)' },
				...baseOptions
			];
		}

		return baseOptions;
	};

	const getValidReasoningEffort = (model, effort) => {
		const validEfforts = supportsNoneReasoningEffort(model)
			? ['none', 'minimal', 'low', 'medium', 'high']
			: ['minimal', 'low', 'medium', 'high'];

		return validEfforts.includes(effort) ? effort : 'medium';
	};

	const getTemperatureForModel = (model, currentTemperature) => {
		return isGpt5Model(model) ? 1.0 : currentTemperature;
	};

	const getDisplayTemperature = (model, currentTemperature) => {
		return isGpt5Model(model) ? 1.0 : currentTemperature;
	};

	const [openAIKey, setOpenAIKey] = useState(openaiKey);
	const [localModel, setLocalModel] = useState(openaiModel || "gpt-4.1-nano");
	const [localPrompt, setLocalPrompt] = useState(openaiPrompt);
	const [localTemperature, setLocalTemperature] = useState(ensureMinimumTemperature(temperature || 0.7));
	const [localTopP, setLocalTopP] = useState(topP || 1);
	const [localReasoningEffort, setLocalReasoningEffort] = useState(reasoningEffort || "medium");
	const [localVerbosity, setLocalVerbosity] = useState(verbosity || "medium");
	const [localWebSearchEnabled, setLocalWebSearchEnabled] = useState(webSearchEnabled || false);
	const [localKnowledgeBaseEnabled, setLocalKnowledgeBaseEnabled] = useState(knowledgeBaseEnabled || false);
	const [isExamplesModalOpen, setIsExamplesModalOpen] = useState(false);
	const [isKnowledgeBaseOpen, setIsKnowledgeBaseOpen] = useState(false);
	const [fileCount, setFileCount] = useState(0);
	const [showApiKeyChangeWarning, setShowApiKeyChangeWarning] = useState(false);

	const proceedWithSave = () => {
		const finalTemperature = getTemperatureForModel(localModel, localTemperature);
		onSave({
			openaiKey: openAIKey,
			temperature: finalTemperature,
			topP: localTopP,
			openaiModel: localModel,
			openaiPrompt: localPrompt,
			reasoningEffort: localReasoningEffort,
			verbosity: localVerbosity,
			webSearchEnabled: localWebSearchEnabled,
			knowledgeBaseEnabled: localKnowledgeBaseEnabled,
		});
		setShowApiKeyChangeWarning(false);
	};

	const handleSave = () => {
		const apiKeyChanged = openAIKey?.trim() !== openaiKey?.trim();
		const hasFiles = fileCount > 0;
		const hasNewKey = openAIKey?.trim();

		if (apiKeyChanged && hasFiles && hasNewKey) {
			setShowApiKeyChangeWarning(true);
			return;
		}

		proceedWithSave();
	};

	useEffect(() => {
		if (isOpen) {
			setOpenAIKey(openaiKey || "");
			setLocalModel(openaiModel || "gpt-4o");
			setLocalPrompt(openaiPrompt || "");
			setLocalTemperature(ensureMinimumTemperature(temperature || 0.7));
			setLocalTopP(topP || 1);
			setLocalWebSearchEnabled(webSearchEnabled || false);
			setLocalKnowledgeBaseEnabled(knowledgeBaseEnabled || false);
		}
	}, [isOpen, openaiKey, openaiModel, openaiPrompt, temperature, topP, webSearchEnabled, knowledgeBaseEnabled]);

	useEffect(() => {
		if (isOpen && instanceId) {
			ipcRenderer.invoke(channels.GET_OPENAI_KNOWLEDGE_STATUS, instanceId).then(result => {
				if (result.success) {
					setFileCount(result.fileCount || 0);
				}
			}).catch(err => {
				console.error('Error loading OpenAI knowledge base status:', err);
			});
		}
	}, [isOpen, instanceId]);

	useEffect(() => {
		if (isGpt5Model(localModel) && localKnowledgeBaseEnabled) {
			setLocalKnowledgeBaseEnabled(false);
		}
		const validEffort = getValidReasoningEffort(localModel, localReasoningEffort);
		if (validEffort !== localReasoningEffort) {
			setLocalReasoningEffort(validEffort);
		}
	}, [localModel]);

	return (
		<>
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogPortal>
					<DialogOverlay className="z-[8999] bg-black/80 backdrop-blur-md" />
					<DialogContent
						className="max-w-[850px] bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-black/50 relative overflow-hidden"
						style={{
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 9001
						}}
					>
					{/* Efeitos de fundo glassmorphism com gradiente escuro mais sutil */}
					<div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
					<div className="absolute inset-0 bg-gradient-to-r from-transparent via-modalOverlayStrong to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1500 pointer-events-none"></div>
					<div className="absolute top-16 left-24 w-40 h-40 bg-effectBlue rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>
					<div className="absolute bottom-24 right-20 w-48 h-48 bg-effectPurple rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>
					<div className="absolute top-40 right-40 w-28 h-28 bg-effectIndigo rounded-full blur-2xl animate-pulse pointer-events-none" style={{ animationDuration: '6s', animationDelay: '4s' }}></div>
					<div className="absolute bottom-16 left-16 w-36 h-36 bg-effectCyan rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '7s', animationDelay: '3s' }}></div>

					{/* Botão X customizado */}
					<button
						onClick={onClose}
						className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-white hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
					>
						<X className="w-4 h-4" />
					</button>

					<DialogHeader className="relative z-10">
						<DialogTitle className="flex items-center gap-4 text-white">
							<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800/80 via-statusInfo/30 to-purplePrimary/40 shadow-lg border border-white/20">
								<span className="text-xl drop-shadow-sm">🤖</span>
								<div className="absolute inset-0 bg-gradient-to-br from-statusInfo/10 to-purplePrimary/8 rounded-xl animate-pulse-subtle"></div>
							</div>
							<span className="text-xl font-semibold">Configurações do GPT</span>
						</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-6 relative z-10 overflow-y-auto max-h-[calc(100vh-220px)] pr-2">
						<div>
							<label
								htmlFor="openai-key"
								className="block text-sm font-medium text-textOnDarkStrong mb-2"
							>
								OpenAI Key
							</label>
							<input
								id="openai-key"
								type="text"
								value={openAIKey}
								onChange={(e) => setOpenAIKey(e.target.value)}
								className="w-full rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText"
								placeholder="sk-..."
							/>
							{fileCount > 0 && (
								<div className="mt-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
									<span className="text-yellow-500 text-sm mt-0.5">⚠️</span>
									<p className="text-xs text-yellow-300 leading-relaxed">
										<strong>ATENÇÃO:</strong> Você possui {fileCount} arquivo{fileCount > 1 ? 's' : ''} na base de conhecimento.
										Alterar a chave API fará você perder acesso a {fileCount > 1 ? 'estes arquivos' : 'este arquivo'}.
									</p>
								</div>
							)}
						</div>

						<div>
							<label
								htmlFor="openai-model"
								className="block text-sm font-medium text-textOnDarkStrong mb-2"
							>
								Modelo do GPT
							</label>
							<select
								id="openai-model"
								value={localModel}
								onChange={(e) => setLocalModel(e.target.value)}
								className="w-full rounded-lg border border-emerald-500/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emerald-400 focus:ring focus:ring-emerald-400/30 shadow-lg appearance-none cursor-pointer"
								style={{
									backgroundImage: `url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")`,
									backgroundPosition: 'right 0.5rem center',
									backgroundRepeat: 'no-repeat',
									backgroundSize: '1.5em 1.5em'
								}}
							>
								<optgroup label="GPT-5.4" className="bg-slate-800 text-white">
									<option value="gpt-5.4" className="bg-slate-800 text-white">GPT-5.4</option>
									<option value="gpt-5.4-mini" className="bg-slate-800 text-white">GPT-5.4 Mini</option>
									<option value="gpt-5.4-nano" className="bg-slate-800 text-white">GPT-5.4 Nano</option>
								</optgroup>
								<optgroup label="GPT-5.2" className="bg-slate-800 text-white">
									<option value="gpt-5.2" className="bg-slate-800 text-white">GPT-5.2</option>
								</optgroup>
								<optgroup label="GPT-5.1" className="bg-slate-800 text-white">
									<option value="gpt-5.1" className="bg-slate-800 text-white">GPT-5.1</option>
									<option value="gpt-5.1-2025-11-13" className="bg-slate-800 text-white">GPT-5.1-2025-11-13</option>
								</optgroup>
								<optgroup label="GPT-5" className="bg-slate-800 text-white">
									<option value="gpt-5" className="bg-slate-800 text-white">GPT-5</option>
									<option value="gpt-5-mini" className="bg-slate-800 text-white">GPT-5 Mini</option>
									<option value="gpt-5-nano" className="bg-slate-800 text-white">GPT-5 Nano</option>
								</optgroup>
								<optgroup label="GPT-4.1" className="bg-slate-800 text-white">
									<option value="gpt-4.1-nano" className="bg-slate-800 text-white">GPT-4.1 Nano</option>
									<option value="gpt-4.1-mini" className="bg-slate-800 text-white">GPT-4.1 Mini</option>
									<option value="gpt-4.1" className="bg-slate-800 text-white">GPT-4.1</option>
								</optgroup>
								<optgroup label="GPT-4o" className="bg-slate-800 text-white">
									<option value="gpt-4o" className="bg-slate-800 text-white">GPT-4o</option>
									<option value="gpt-4o-mini" className="bg-slate-800 text-white">GPT-4o Mini</option>
								</optgroup>
							</select>
						</div>

						{!isGpt5Model(localModel) && (
							<>
								<div>
									<label
										htmlFor="temperature"
										className="block text-sm font-medium text-textOnDarkStrong mb-2"
									>
										Temperatura: {localTemperature.toFixed(2)}
									</label>
									<div className="flex items-center">
										<span className="mr-2 text-xs text-gray-400">0.01</span>
										<input
											id="temperature"
											type="range"
											min="0.01"
											max="2"
											step="0.01"
											value={localTemperature}
											onChange={(e) => setLocalTemperature(ensureMinimumTemperature(e.target.value))}
											className="w-full h-2 bg-modalOverlaySoft backdrop-blur-xl rounded-lg appearance-none cursor-pointer accent-emerald-400 shadow-lg border border-emerald-500/20"
										/>
										<span className="ml-2 text-xs text-gray-400">2.0</span>
									</div>
									<div className="mt-1 text-xs text-center text-gray-400">
										{localTemperature <= 0.1 && 'Mais conservador e previsível'}
										{localTemperature > 0.1 && localTemperature < 1 && 'Equilibrado entre previsibilidade e criatividade'}
										{localTemperature >= 1 && localTemperature < 1.5 && 'Mais criativo e diverso'}
										{localTemperature >= 1.5 && 'Altamente criativo e imprevisível'}
									</div>
								</div>

								<div>
									<label
										htmlFor="top-p"
										className="block text-sm font-medium text-textOnDarkStrong mb-2"
									>
										Top P: {localTopP.toFixed(2)}
									</label>
									<div className="flex items-center">
										<span className="mr-2 text-xs text-gray-400">0.0</span>
										<input
											id="top-p"
											type="range"
											min="0"
											max="1"
											step="0.05"
											value={localTopP}
											onChange={(e) => setLocalTopP(parseFloat(e.target.value))}
											className="w-full h-2 bg-modalOverlaySoft backdrop-blur-xl rounded-lg appearance-none cursor-pointer accent-emerald-400 shadow-lg border border-emerald-500/20"
										/>
										<span className="ml-2 text-xs text-gray-400">1.0</span>
									</div>
									<div className="mt-1 text-xs text-center text-gray-400">
										Controla a diversidade das respostas (valores menores = mais focado)
									</div>
								</div>
							</>
						)}

						{isGpt5Model(localModel) && (
							<>
								<div>
									<label
										htmlFor="reasoning-effort"
										className="block text-sm font-medium text-textOnDarkStrong mb-2"
									>
										Reasoning Effort
									</label>
									<select
										id="reasoning-effort"
										value={localReasoningEffort}
										onChange={(e) => setLocalReasoningEffort(e.target.value)}
										className="w-full rounded-lg border border-emerald-500/25 bg-modalOverlaySoft backdrop-blur-xl p-3 text-white focus:border-emerald-400 focus:ring focus:ring-emerald-400/30 shadow-lg cursor-pointer"
									>
									{getReasoningEffortOptions(localModel).map((option) => (
										<option key={option.value} value={option.value} className="bg-slate-800 text-white">
											{option.label}
										</option>
									))}
									</select>
									<div className="mt-1 text-xs text-center text-gray-400">
										Controla quantos tokens de raciocínio o modelo gera antes da resposta
									</div>
								</div>

								<div>
									<label
										htmlFor="verbosity"
										className="block text-sm font-medium text-textOnDarkStrong mb-2"
									>
										Verbosity (Output Length)
									</label>
									<select
										id="verbosity"
										value={localVerbosity}
										onChange={(e) => setLocalVerbosity(e.target.value)}
										className="w-full rounded-lg border border-emerald-500/25 bg-modalOverlaySoft backdrop-blur-xl p-3 text-white focus:border-emerald-400 focus:ring focus:ring-emerald-400/30 shadow-lg cursor-pointer"
									>
										<option value="low" className="bg-slate-800 text-white">Low (Concise responses)</option>
										<option value="medium" className="bg-slate-800 text-white">Medium (Balanced)</option>
										<option value="high" className="bg-slate-800 text-white">High (Detailed explanations)</option>
									</select>
									<div className="mt-1 text-xs text-center text-gray-400">
										Controla o nível de detalhamento e tamanho da resposta
									</div>
								</div>
							</>
						)}

						<div
							className="rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl p-4 cursor-pointer hover:border-emeraldBase/40 transition-all duration-200"
							onClick={() => {
								setLocalWebSearchEnabled(!localWebSearchEnabled);
							}}
						>
							<div className="flex items-center justify-between gap-4">
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										<Globe className="w-4 h-4 text-statusInfo" />
										<span className="text-sm font-medium text-white">Pesquisa na Web</span>
									</div>
									<p className="text-xs text-gray-300 leading-relaxed">
										Permite ao GPT buscar informações na web quando necessário.
										Útil para dados atualizados, eventos recentes e informações em tempo real.
									</p>
								</div>
								<div className="relative inline-flex items-center pointer-events-none">
									<input
										type="checkbox"
										checked={localWebSearchEnabled}
										onChange={() => {}}
										className="sr-only peer"
										tabIndex={-1}
									/>
									<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
								</div>
							</div>
						</div>

						<div className="rounded-lg border border-purple-500/25 bg-modalOverlaySoft backdrop-blur-xl p-4">
							<div
								className={`flex items-center justify-between gap-4 mb-3 ${isGpt5Model(localModel) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
								onClick={() => {
									if (!isGpt5Model(localModel)) {
										setLocalKnowledgeBaseEnabled(!localKnowledgeBaseEnabled);
									}
								}}
							>
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										<Database className="w-4 h-4 text-purple-400" />
										<span className="text-sm font-medium text-white">Base de Conhecimento</span>
										{fileCount > 0 && (
											<span className="px-2 py-0.5 text-xs font-medium bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30">
												{fileCount} arquivo(s)
											</span>
										)}
									</div>
									<p className="text-xs text-gray-300 leading-relaxed">
										Permite ao GPT consultar documentos enviados (PDFs, DOCX, etc) durante conversas.
										Use para criar uma base de conhecimento personalizada.
									</p>
									{isGpt5Model(localModel) && (
										<div className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-2">
											<span className="text-red-500 text-xs">🚫</span>
											<p className="text-xs text-red-300 leading-relaxed">
												O modelo {localModel} não suporta Base de Conhecimento. Use GPT-4o, GPT-4o-mini ou GPT-4-turbo para esta funcionalidade.
											</p>
										</div>
									)}
								</div>
								<div
									className="relative inline-flex items-center cursor-pointer"
								>
									<input
										type="checkbox"
										checked={localKnowledgeBaseEnabled}
										onChange={() => {}}
										disabled={isGpt5Model(localModel)}
										className="sr-only peer"
										tabIndex={-1}
									/>
									<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
								</div>
							</div>
							<button
								type="button"
								onClick={() => {
									if (!openAIKey || openAIKey.trim() === '') {
										alert('Configure a API Key do OpenAI antes de gerenciar arquivos da base de conhecimento.');
										return;
									}
									setIsKnowledgeBaseOpen(true);
								}}
								disabled={!localKnowledgeBaseEnabled || isGpt5Model(localModel)}
								className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600/20 to-purple-500/20 border border-purple-500/30 text-purple-300 transition-all text-sm hover:from-purple-600/30 hover:to-purple-500/30 hover:text-purple-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-purple-600/20 disabled:hover:to-purple-500/20"
							>
								<Database className="w-4 h-4" />
								<span>{isGpt5Model(localModel) ? 'Indisponível para GPT-5' : 'Gerenciar Arquivos'}</span>
							</button>
						</div>						<div>
							<div className="flex items-center justify-between mb-2">
								<label
									htmlFor="openai-prompt"
									className="block text-sm font-medium text-textOnDarkStrong"
								>
									Prompt do GPT
								</label>
								<button
									type="button"
									onClick={() => setIsExamplesModalOpen(true)}
									className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-statusInfo/20 to-purplePrimary/20 hover:from-statusInfo/30 hover:to-purplePrimary/30 border border-statusInfo/30 text-statusInfo hover:text-white transition-all duration-300 hover:scale-105 text-xs font-medium"
								>
									<BookOpen className="w-3.5 h-3.5" />
									<span>Exemplos de Prompts</span>
								</button>
							</div>
							<textarea
								id="openai-prompt"
								className="w-full h-40 rounded-lg border border-emerald-500/25 bg-modalOverlaySoft backdrop-blur-xl p-4 text-white focus:border-emerald-400 focus:ring focus:ring-emerald-400/30 shadow-lg placeholder-gray-400"
								value={localPrompt}
								onChange={(e) => setLocalPrompt(e.target.value)}
								placeholder="Você é um assistente útil..."
							/>
						</div>

						<div className="flex justify-end items-center pt-4 mt-2 relative z-10">
							<div className="flex space-x-3">
								<button
									type="button"
									onClick={onClose}
									className="relative rounded-xl text-white px-4 py-3 font-medium transition-all duration-300 bg-gradient-to-r from-dangerFrom to-dangerTo hover:from-red-400 hover:to-red-500 shadow-lg hover:shadow-red-400/25 hover:scale-[1.02] cursor-pointer"
								>
									<span className="relative z-10">Cancelar</span>
								</button>
								<button
									onClick={handleSave}
									type="button"
									className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600/80 to-cyan-600/80 backdrop-blur-xl border border-emerald-400/60 px-4 py-3 text-sm font-semibold text-white transition-all duration-300 hover:from-emerald-500/90 hover:to-cyan-500/90 hover:border-emerald-300/70 hover:shadow-xl hover:shadow-emerald-500/40 shadow-lg shadow-emerald-500/25 hover:scale-[1.02]"
								>
									<svg className="h-5 w-5 drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
									</svg>
									<span>Salvar</span>
								</button>
							</div>
						</div>
					</div>
					</DialogContent>
				</DialogPortal>
			</Dialog>

			<PromptExamplesModal
				isOpen={isExamplesModalOpen}
				onClose={() => setIsExamplesModalOpen(false)}
			/>

			<KnowledgeBaseSection
				isOpen={isKnowledgeBaseOpen}
				onClose={() => {
					setIsKnowledgeBaseOpen(false);
					if (isOpen && instanceId) {
						ipcRenderer.invoke(channels.GET_OPENAI_KNOWLEDGE_STATUS, instanceId).then(result => {
							if (result.success) {
								setFileCount(result.fileCount || 0);
							}
						}).catch(err => {
							console.error('Error reloading OpenAI knowledge base status:', err);
						});
					}
				}}
				onChange={() => {
					if (instanceId) {
						ipcRenderer.invoke(channels.GET_OPENAI_KNOWLEDGE_STATUS, instanceId).then(result => {
							if (result.success) {
								setFileCount(result.fileCount || 0);
							}
						}).catch(err => {
							console.error('Error updating file count:', err);
						});
					}
				}}
				instanceId={instanceId}
				aiProvider="GPT"
			/>

			<Dialog open={showApiKeyChangeWarning} onOpenChange={setShowApiKeyChangeWarning}>
				<DialogContent className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-red-500/30 shadow-2xl shadow-red-500/20 max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-xl font-bold text-white">
							<span className="text-2xl">⚠️</span>
							Confirmar Alteração de Chave API
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 pt-4">
						<p className="text-gray-300 text-sm leading-relaxed">
							Você está alterando a chave API do OpenAI e possui <strong className="text-yellow-300">{fileCount} arquivo{fileCount > 1 ? 's' : ''}</strong> registrado{fileCount > 1 ? 's' : ''} na base de conhecimento.
						</p>
						<div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-2">
							<p className="text-red-400 font-semibold text-sm">⚠️ CONSEQUÊNCIAS:</p>
							<ul className="text-gray-300 text-sm space-y-1.5 list-disc list-inside">
								<li>A nova chave <strong>NÃO terá acesso</strong> aos arquivos da chave anterior</li>
								<li>Você precisará <strong>enviar todos os arquivos novamente</strong></li>
								<li>Os arquivos não serão deletados, apenas <strong>inacessíveis</strong></li>
							</ul>
						</div>
						<p className="text-gray-400 text-sm">
							Deseja continuar com a alteração?
						</p>
						<div className="flex gap-3 pt-2">
							<button
								onClick={() => setShowApiKeyChangeWarning(false)}
								className="flex-1 px-4 py-2.5 rounded-lg bg-gray-600 hover:bg-gray-500 text-white font-medium transition-colors"
							>
								Cancelar
							</button>
							<button
								onClick={proceedWithSave}
								className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors"
							>
								Continuar
							</button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};
